//@ts-nocheck
import { useEffect, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toLonLat } from 'ol/proj';
import { X, Save, Pencil, Trash2, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { useMapStore } from '@/stores/mapStore';
import {
  useCreateFieldNote,
  useUpdateFieldNote,
  useDeleteFieldNote,
} from '@/hooks/api/useFieldNotes';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from '@/components/ui/card';

import { useShallow } from 'zustand/react/shallow';
import { useMapOverlayPosition } from '../../hooks/useMapOverlayPosition';
import { useSelectionStore } from '@/stores/selectionStore';
import { SystemLayerKey } from '@packages/database/types';

interface FieldNoteFormValues {
  content: string;
}

export const FieldNotePopup = () => {
  const popupRef = useRef<HTMLDivElement>(null);

  // Interaction mode + marker still live in mapStore
  const interactionMode = useMapStore((s) => s.interactionMode);
  const mapMarkerLocation = useSelectionStore((s) => s.markerLocation);
  const resetInteraction = useMapStore((s) => s.resetInteraction);

  // Active field note comes from selectionStore
  const activeNoteFeature = useSelectionStore(
    (s) =>
      s.selected.length === 1 &&
      s.selected.find((f) => f.layerKey === SystemLayerKey.FIELD_NOTE),
  );
  const { clear } = useSelectionStore();

  const isEditing = !!activeNoteFeature;
  const isCreating = interactionMode === 'addNote' && !!mapMarkerLocation;
  const isOpen = isEditing || isCreating;

  // Position — creating uses marker, editing uses feature coordinate
  const positionCoordinate = useMemo(() => {
    if (isCreating && mapMarkerLocation) return mapMarkerLocation.coordinate;
    if (isEditing) return activeNoteFeature?.coordinate ?? null;
    return null;
  }, [isCreating, isEditing, mapMarkerLocation, activeNoteFeature]);

  useMapOverlayPosition({
    position: positionCoordinate,
    elementRef: popupRef,
    isOpen,
    offset: [0, -12],
  });

  const { createFieldNote, isCreating: apiCreating } = useCreateFieldNote();
  const { updateFieldNote, isUpdating: apiUpdating } = useUpdateFieldNote();
  const { deleteFieldNote, isDeleting: apiDeleting } = useDeleteFieldNote();
  const isSubmitting = apiCreating || apiUpdating || apiDeleting;

  const { register, handleSubmit, reset, setValue, setFocus } = useForm<{
    content: string;
  }>();

  useEffect(() => {
    if (!isOpen) return;
    if (isEditing && activeNoteFeature?.data) {
      setValue('content', activeNoteFeature.data.content as string);
    } else {
      reset({ content: '' });
    }
    setTimeout(() => setFocus('content'), 50);
  }, [isOpen, isEditing, activeNoteFeature]);

  const handleClose = () => {
    if (isCreating) resetInteraction();
    clear();
  };

  const onSubmit = (data: { content: string }) => {
    if (isEditing && activeNoteFeature?.id) {
      updateFieldNote(
        { id: activeNoteFeature.id, content: data.content },
        {
          onSuccess: () => {
            toast.success('Note updated');
            handleClose();
          },
        },
      );
    } else if (isCreating && mapMarkerLocation) {
      const [lon, lat] = toLonLat(mapMarkerLocation.coordinate);
      createFieldNote(
        {
          content: data.content,
          location: {
            latitude: lat,
            longitude: lon,
          },
        },

        {
          onSuccess: () => {
            toast.success('Note created');
            handleClose();
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (!activeNoteFeature?.id) return;
    deleteFieldNote(activeNoteFeature.id, {
      onSuccess: () => {
        toast.success('Note deleted');
        handleClose();
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      ref={popupRef}
      className="absolute top-0 left-0 z-50 will-change-transform"
      style={{ display: 'none' }}
    >
      <Card className="w-72 py-2 shadow-xl border-border overflow-hidden ring-1 ring-ring-secondary">
        <div className="flex items-center justify-between px-3 py-1.5 bg-card border-b border-border">
          <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
            {isEditing ? (
              <Pencil size={12} className="text-secondary" />
            ) : (
              <StickyNote size={12} className="text-secondary" />
            )}
            {isEditing ? 'Edit Note' : 'Add Note'}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-muted-foreground"
            onClick={handleClose}
          >
            <X size={12} />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-3">
            <Textarea
              {...register('content', { required: true })}
              placeholder="Enter note details..."
              className="min-h-[60px] resize-none border-border text-xs bg-background"
              disabled={isSubmitting}
              autoFocus
            />
          </div>
          <div className="flex items-center justify-end gap-2 p-2">
            {isEditing && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-red-500 hover:bg-red-500/10 mr-auto"
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                <Trash2 size={14} />
              </Button>
            )}
            <Button
              size="sm"
              type="submit"
              disabled={isSubmitting}
              className="h-7 px-3 text-xs gap-1.5 bg-secondary text-white"
            >
              <Save className="h-3 w-3" />
              {isEditing ? 'Update' : 'Save'}
            </Button>
          </div>
        </form>
      </Card>
      <div className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 w-3 h-3 bg-card border-r border-b border-border rotate-45" />
    </div>
  );
};
